import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import React from 'react'
import { useTranslation } from 'react-i18next'

import type { IngredientGroupListItem } from '@/api/types'
import { useAuth } from '@/auth'
import { Button } from '@/components/Button'
import { PageEditHeader } from '@/components/PageEditHeader'
import { CompactTable } from '@/components/Table'

type Props = {
  ingredientGroups: IngredientGroupListItem[]
}

export default function IngredientsGroupPanel({ ingredientGroups }: Props) {
  const { t } = useTranslation()
  const { isAdmin, isNutritionist, isAmbassador } = useAuth()
  const navigate = useNavigate()
  const searchParams = useSearch({ from: '/ingredients/displaynames' })

  const canCreate = isAdmin || isNutritionist || isAmbassador

  const handleSearchChange = (query: string) => {
    navigate({
      to: '/ingredients',
      search: { search: query || undefined },
      hash: 'Ings',
    })
  }

  const rows = ingredientGroups.map((item) => ({
    idIngredientGroup: item.idIngredientGroup,
    name: item.name,
    count: item.ingredientCount,
  }))

  return (
    <>
      <PageEditHeader title={t('ingredient.ingredientGroups')} style={{ marginTop: 32 }}>
        {canCreate && (
          <Button variant="primary" asChild>
            <Link to="/ingredients/displaynames/new">{t('ingredient.newIngredientGroup')}</Link>
          </Button>
        )}
      </PageEditHeader>
      <CompactTable
        aria-label="Ingredients list"
        search={{
          columns: ['name'],
          defaultValue: searchParams.search,
          onSearchChange: handleSearchChange,
        }}
        columns={[
          {
            label: t('ingredient.name'),
            key: 'name',
          },
          { label: t('ingredient.count'), key: 'count', width: 100 },
        ]}
        rows={rows}
        onLinkAction={(row) => {
          return `/ingredients/displaynames/${row.idIngredientGroup}`
        }}
        sorting
      />
    </>
  )
}
